import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { XCircle, Loader2, Sparkles, FileSignature, Download, Copy, Check } from "lucide-react";
import { AIText } from "@/components/AIText";

export default function Cancellation() {
  const { t, lang } = useI18n();
  const [policies, setPolicies] = useState([]);
  const [docs, setDocs] = useState([]);
  const [source, setSource] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [loadingA, setLoadingA] = useState(false);
  const [letterForm, setLetterForm] = useState({ carrier: "", policy_number: "", effective_date: "", reason: "" });
  const [letter, setLetter] = useState("");
  const [loadingL, setLoadingL] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/policies").then((r) => setPolicies(r.data));
    api.get("/documents").then((r) => setDocs(r.data.filter((d) => d.analysis)));
  }, []);

  const analyze = async () => {
    if (!source) { toast.error(t("choose_policy_doc")); return; }
    setLoadingA(true); setAnalysis("");
    const [kind, id] = source.split(":");
    try {
      const payload = kind === "doc" ? { document_id: id, language: lang } : { policy_id: id, language: lang };
      const r = await api.post("/cancellation/analyze", payload);
      setAnalysis(r.data.answer);
      // prefill letter fields
      if (kind === "pol") {
        const p = policies.find((x) => x.id === id);
        if (p) setLetterForm((f) => ({ ...f, carrier: p.carrier || "", policy_number: p.policy_number || "" }));
      } else {
        const d = docs.find((x) => x.id === id);
        if (d?.analysis) setLetterForm((f) => ({ ...f, carrier: d.analysis.carrier || "", policy_number: d.analysis.policy_number || "" }));
      }
    } catch { toast.error("Analysis failed"); } finally { setLoadingA(false); }
  };

  const genLetter = async () => {
    if (!letterForm.carrier) { toast.error(t("enter_insurer")); return; }
    setLoadingL(true); setLetter("");
    try { const r = await api.post("/cancellation/letter", { ...letterForm, language: lang }); setLetter(r.data.letter); }
    catch { toast.error("Could not generate letter"); } finally { setLoadingL(false); }
  };

  const copy = async () => { try { await navigator.clipboard.writeText(letter); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.message("Select the text to copy it."); } };
  const exportPdf = () => downloadReport({ title: "Cancellation Request Letter", language: lang,
    subtitle: letterForm.carrier, sections: [{ heading: "Draft letter", body: letter }] });

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><XCircle className="h-7 w-7 text-brandblue" /> {t("cancellation_title")}</h1>
      <p className="mt-1 text-slate-500">{t("cancellation_sub")}</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <h2 className="text-lg font-semibold text-navy">{t("can_i_cancel")}</h2>
            <div className="mt-3">
              <Label>{t("which_policy")}</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="cancel-source" className="mt-1.5 rounded-xl"><SelectValue placeholder={t("select_policy_doc")} /></SelectTrigger>
                <SelectContent>
                  {policies.map((p) => <SelectItem key={p.id} value={`pol:${p.id}`}>{t("cat_" + (p.category || "").toLowerCase())} · {p.carrier || "—"} ({t("wallet_tag")})</SelectItem>)}
                  {docs.map((d) => <SelectItem key={d.id} value={`doc:${d.id}`}>{d.original_filename} ({t("document_tag")})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="cancel-analyze-btn" onClick={analyze} disabled={loadingA} className="mt-4 rounded-full brand-gradient text-white">
              {loadingA ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("analyzing")}</> : <><Sparkles className="h-4 w-4 mr-2" />{t("analyze_cancellation")}</>}
            </Button>
            {analysis && <div className="mt-4 rounded-xl bg-slate-50 p-4"><AIText>{analysis}</AIText></div>}
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <h2 className="text-lg font-semibold text-navy flex items-center gap-2"><FileSignature className="h-5 w-5 text-brandteal" /> {t("draft_letter_step")}</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>{t("insurer_carrier")}</Label><Input data-testid="letter-carrier" value={letterForm.carrier} onChange={(e) => setLetterForm({ ...letterForm, carrier: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("policy_number")}</Label><Input data-testid="letter-policy" value={letterForm.policy_number} onChange={(e) => setLetterForm({ ...letterForm, policy_number: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("cancellation_date")}</Label><Input data-testid="letter-date" type="date" value={letterForm.effective_date} onChange={(e) => setLetterForm({ ...letterForm, effective_date: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div className="sm:col-span-2"><Label>{t("reason_optional")}</Label><Input data-testid="letter-reason" value={letterForm.reason} onChange={(e) => setLetterForm({ ...letterForm, reason: e.target.value })} className="mt-1 rounded-xl" placeholder={t("reason_ph")} /></div>
          </div>
          <Button data-testid="letter-generate-btn" onClick={genLetter} disabled={loadingL} className="mt-4 rounded-full brand-gradient text-white">
            {loadingL ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("drafting")}</> : <><FileSignature className="h-4 w-4 mr-2" />{t("generate_letter")}</>}
          </Button>
          {letter && (
            <div className="mt-4">
              <div className="flex gap-2 mb-2">
                <Button data-testid="letter-copy" variant="outline" onClick={copy} className="rounded-full h-9">{copied ? <><Check className="h-4 w-4 mr-1" />{t("copied")}</> : <><Copy className="h-4 w-4 mr-1" />{t("copy")}</>}</Button>
                <Button data-testid="letter-export" variant="outline" onClick={exportPdf} className="rounded-full h-9"><Download className="h-4 w-4 mr-1" />{t("export_pdf")}</Button>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{letter}</div>
              <p className="mt-2 text-xs text-slate-400">{t("cancellation_note")}</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
